// Custom hook for rendering chat messages - Obsidian design system.
import React, { useEffect, useRef, useState } from 'react';
import { MessageWithCitations } from './chatTypes';
import { retrieveFile } from './fileDatabase';
import GeneratedImageMessage from './GeneratedImageMessage';
import UserMessageWithImage from './UserMessageWithImage';
import { LazyTable, isLargeTable, processContent } from './contentRendering';
import { isImageGenerationModel } from './chatUtils';
import { domainOf, localizeStepLabel } from './agentTrace';
import config from '../../config';

const BASE_URL = config.apiUrl;

export interface MessageRendererDependencies {
  theme: 'light' | 'dark';
  currentThemeColors: any;
  messageStyles: any;
  copiedMessageId: string | null;
  branchingMessageId: number | null;
  openModelSwitchId: string | null;
  copiedCode: string | null;
  isMobile: boolean;
  messages: MessageWithCitations[];  // Full thread - used to detect the last message (suggestion-chip gate)
  textareaRef: React.RefObject<HTMLTextAreaElement>;  // Composer input, for the suggestion-chip fill+focus
  getModelDisplayName: (model: string) => string;
  convertCitationsToMap: (citations?: Set<string>) => { [key: string]: any } | undefined;
  copyToClipboard: (text: string) => void;
  // True when the open conversation belongs to another team member (Teams
  // phase 2 read-only view). Hides the 👍/👎 feedback buttons: the server
  // already 403s feedback on someone else's messages, so showing them only
  // produced a control that silently reverted. Optional/absent = not read-only.
  readOnly?: boolean;
}

export interface MessageRendererCallbacks {
  setCopiedMessageId: (id: string | null) => void;
  setBranchingMessageId: (id: number | null) => void;
  setOpenModelSwitchId: (id: string | null) => void;
  setPopupPosition: (position: { top: number; left: number } | null) => void;
  setInputMessage: (message: string) => void;  // Fills the composer when a suggestion chip is clicked
  handleBranchout: (message: MessageWithCitations) => void;
  navigate: (path: string) => void;
}

// ---- helpers ----------------------------------------------------------------
// Server timestamps are naive UTC - append 'Z' so they render in local time.
const parseServerDate = (s: string): Date => {
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/.test(s);
  return new Date(hasTz ? s : s.replace(' ', 'T') + 'Z');
};

const formatClock = (s?: string): string => {
  if (!s) return '';
  const d = parseServerDate(s);
  if (isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

// ---- byline: orb (brand mark chip + real Power AI logo) + "Power AI" + mono time,
// Chip background and logo asset both come from theme tokens so light mode gets
// a light chip with the dark logo (and vice versa).
const AssistantByline: React.FC<{ c: any; name: string; when?: string }> = ({ c, name, when }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    <span style={{
      width: '20px', height: '20px', borderRadius: '6px',
      background: c.markBg,
      border: `1px solid ${c.line2}`, display: 'grid', placeItems: 'center', flexShrink: 0,
    }}>
      <img
        src={c.logoSrc}
        alt=""
        style={{ width: '12px', height: '12px', objectFit: 'contain', display: 'block' }}
      />
    </span>
    <span style={{ fontWeight: 600, fontSize: '12px', color: c.ink2 }}>{name}</span>
    {when && (
      <span style={{ fontFamily: c.mono, fontWeight: 400, fontSize: '11px', color: c.ink3 }}>{when}</span>
    )}
  </div>
);

// Thread progress: header always visible, steps open while the run is live
// (and for the session that just finished). Historic reloads stay collapsed.
// The right inspector shows the same list; this is the in-thread copy.
const WorkBar: React.FC<{ c: any; message: MessageWithCitations }> = ({ c, message }) => {
  const steps = message.steps || [];
  const streaming = message.isComplete === false;
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef<number>(performance.now());
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const stepsOpen = streaming || (openOverride ?? message.fromServer !== true);

  useEffect(() => {
    if (!streaming) return;
    startRef.current = performance.now();
    const id = window.setInterval(() => {
      setElapsed((performance.now() - startRef.current) / 1000);
    }, 100);
    return () => window.clearInterval(id);
  }, [streaming]);

  const doneSecs = typeof message.traceDurationMs === 'number' ? message.traceDurationMs / 1000 : null;
  const clock = streaming
    ? `${elapsed.toFixed(1)}s`
    : (doneSecs != null ? `${doneSecs.toFixed(1)}s` : null);
  const label = streaming
    ? `Lagi kerja · ${steps.length} langkah`
    : `Riset selesai · ${steps.length} langkah`;
  const toggleable = !streaming && steps.length > 0;

  return (
    <div style={{
      border: `1px solid ${c.line}`,
      background: c.panel,
      borderRadius: '12px',
      width: 'fit-content',
      minWidth: 'min(340px, 100%)',
      maxWidth: '100%',
      overflow: 'hidden',
      animation: 'traceIn 0.25s ease',
    }}>
      <div
        role={toggleable ? 'button' : undefined}
        tabIndex={toggleable ? 0 : undefined}
        aria-expanded={toggleable ? stepsOpen : undefined}
        onClick={toggleable ? () => setOpenOverride(!stepsOpen) : undefined}
        onKeyDown={toggleable ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpenOverride(!stepsOpen);
          }
        } : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          fontSize: '12.5px',
          fontWeight: 500,
          color: c.ink2,
          cursor: toggleable ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        {streaming ? (
          <span style={{
            width: '11px', height: '11px', borderRadius: '50%',
            border: `2px solid ${c.line2}`, borderTopColor: c.jade,
            display: 'inline-block', animation: 'spin 0.7s linear infinite', flexShrink: 0,
          }} />
        ) : (
          <span style={{
            width: '13px', height: '13px', borderRadius: '50%',
            background: c.jadeDim, color: c.jade, display: 'grid', placeItems: 'center',
            fontSize: '8.5px', flexShrink: 0,
          }}>✓</span>
        )}
        <span>{label}</span>
        {clock && (
          <span style={{
            marginLeft: 'auto', fontFamily: c.mono, fontWeight: 400,
            fontSize: '10.5px', color: c.ink3,
          }}>
            {clock}
          </span>
        )}
        {toggleable && (
          <svg
            width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke={c.ink3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            style={{
              flexShrink: 0,
              transform: stepsOpen ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms ease',
            }}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        )}
      </div>

      {streaming && (
        <div className="pa-trace-shimmer" style={{ position: 'relative', height: '1px', overflow: 'hidden', background: c.line }}>
          <span style={{
            position: 'absolute', top: 0, left: 0, height: '100%', width: '30%',
            background: `linear-gradient(90deg, transparent, ${c.jade}, transparent)`,
          }} />
        </div>
      )}

      {steps.length > 0 && stepsOpen && (
        <div style={{
          borderTop: streaming ? 'none' : `1px solid ${c.line}`,
          padding: '8px 12px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}>
          {steps.map((step) => {
            const done = step.status === 'done';
            return (
              <div
                key={step.step_id}
                className="pa-inspector-step"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                  fontSize: '12.5px',
                  color: done ? c.ink3 : c.ink,
                }}
              >
                {done ? (
                  <span style={{
                    width: '14px', height: '14px', borderRadius: '50%',
                    background: c.jadeDim, color: c.jade, display: 'grid',
                    placeItems: 'center', fontSize: '9px', flexShrink: 0, marginTop: '1px',
                  }}>✓</span>
                ) : (
                  <span className="pa-inspector-ping" style={{
                    width: '14px', height: '14px', borderRadius: '50%',
                    border: `2px solid ${c.jade}`, flexShrink: 0, marginTop: '1px',
                  }} />
                )}
                <span style={{
                  textDecoration: done ? 'line-through' : 'none',
                  textDecorationColor: 'rgba(121,127,136,0.7)',
                  minWidth: 0,
                }}>
                  {localizeStepLabel(step.label)}
                  {step.detail && (
                    <span style={{
                      display: 'block',
                      fontFamily: c.mono,
                      fontSize: '11px',
                      color: c.ink3,
                      fontWeight: 400,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      {step.detail}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ---- source chips (domain squares + expandable "+N") ------------------------
// Collapsed shows 4 chips + a "+N" toggle; clicking expands to the full list
// (wrapping to multiple rows) with a "Sembunyikan" toggle to collapse back.
const SourceChips: React.FC<{ c: any; urls: string[] }> = ({ c, urls }) => {
  const [expanded, setExpanded] = useState(false);
  if (!urls.length) return null;
  const MAX = 4;
  const visible = expanded ? urls : urls.slice(0, MAX);
  const overflow = urls.length - MAX;
  const toggleStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', fontSize: '11px', color: c.ink3,
    border: `1px solid ${c.line}`, background: c.panel, borderRadius: '7px',
    padding: '4px 9px', cursor: 'pointer', fontFamily: 'inherit', lineHeight: 'inherit',
    transition: 'border-color 150ms ease, color 150ms ease',
  };
  return (
    <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
      {visible.map((u, i) => (
        <a
          key={i}
          href={u}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px',
            color: c.ink2, border: `1px solid ${c.line}`, background: c.panel,
            borderRadius: '7px', padding: '4px 9px', textDecoration: 'none',
            maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={c.ink3} strokeWidth="1.8" style={{ flexShrink: 0, display: 'block' }}>
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          {domainOf(u)}
        </a>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(v => !v)}
          style={toggleStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = c.line2;
            e.currentTarget.style.color = c.ink;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = c.line;
            e.currentTarget.style.color = c.ink3;
          }}
        >
          {expanded ? 'Sembunyikan' : `+${overflow}`}
        </button>
      )}
    </div>
  );
};

// ---- follow-up suggestion chips ---------------------------------------------
// Post-answer Indonesian follow-ups (max 3). Clicking fills the composer and
// focuses it - never auto-submits. Obsidian idiom: rounded panel pills, hairline
// border that firms on hover, subtle fade-in (frozen under reduced-motion by the
// global guard in index.css).
const SuggestionChips: React.FC<{ c: any; items: string[]; onPick: (text: string) => void }> = ({ c, items, onPick }) => {
  if (!items.length) return null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px',
      animation: 'traceIn 0.2s ease both',
    }}>
      {items.map((text, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onPick(text)}
          style={{
            background: c.panel,
            border: `1px solid ${c.line}`,
            borderRadius: '999px',
            padding: '6px 12px',
            fontSize: '12.5px',
            color: c.ink2,
            fontFamily: 'inherit',
            lineHeight: 1.3,
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'border-color 150ms ease, color 150ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = c.line2;
            e.currentTarget.style.color = c.ink;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = c.line;
            e.currentTarget.style.color = c.ink2;
          }}
        >
          {text}
        </button>
      ))}
    </div>
  );
};

// ---- per-answer credits label + model-usage tooltip ------------------------
// Strip the vendor prefix (everything up to and including the first '/') so
// "zai-org/GLM-5.2" reads as "GLM-5.2"; leave prefix-less ids untouched.
const shortModelName = (model: string): string => {
  const slash = model.indexOf('/');
  return slash >= 0 ? model.slice(slash + 1) : model;
};

// User-facing packaging of raw provider identifiers: family-level names that
// convey WHICH kind of AI did the work without exposing vendor SKUs or
// internal API routes. Raw ids stay intact in the DB/protocol for analytics.
const packagedModelName = (model: string): string => {
  const m = model.toLowerCase();
  // Suggestions seat FIRST: the backend suffixes its cost-event key with
  // " (saran)" (claude-haiku-4-5 on the Anthropic SDK; gpt-oss/brain when it
  // falls back). Without this branch the fallback row collided with the
  // writer as a second "Penulis · GPT" (live tooltip bug, 2026-07-26).
  if (m.includes('playbook')) return 'Playbook';
  if (m.includes('saran')) return m.includes('claude') ? 'Saran · Claude' : 'Saran · GPT';
  // Smart-title seat: "claude-haiku-4-5 (judul)" - fires once per conversation.
  if (m.includes('judul')) return 'Judul · Claude';
  // Current pair (2026-07-26): gpt-5.6-terra routes, gpt-5.6-luna writes.
  // No id here contains another's key, so branch order below carries no
  // shadowing risk.
  if (m.includes('gpt-5.6-terra')) return 'Mesin Utama · GPT';
  if (m.includes('gpt-6-luna') || m.includes('gpt-5.6-luna')) return 'Penulis · GPT';
  if (m.includes('deepseek')) {
    return m.includes('penulis') ? 'Penulis · DeepSeek' : 'Mesin Utama · DeepSeek';
  }
  // Kept for historical conversations created before DeepSeek replaced GLM.
  if (m.includes('glm')) return 'Mesin Utama · GLM';
  if (m.includes('kimi')) return 'Mesin Utama · Kimi';
  // Old suggestion rows predating the " (saran)" suffix still package as
  // Penulis; acceptable for historical messages.
  if (m.includes('qwen')) return 'Penulis · Qwen';
  if (m.includes('gpt-oss')) return 'Penulis · GPT';
  if (m.includes('opus') || m.includes('sonnet')) return 'Caption · Claude';
  if (m.includes('claude')) return 'Saran · Claude';
  // Gemini only ever appears via the file/image-analysis path (AUTO images →
  // Gemini 3 Flash). "gemini" shares no substring with the brain/writer keys,
  // so neither branch can shadow the other.
  if (m.includes('gemini')) return 'Analisis Gambar · Gemini';
  // "Perplexity" is the brand people know; "Sonar" is its model SKU.
  if (m.includes('serper')) return 'Riset Web · Google';
  if (m.includes('sonar') || m.includes('perplexity')) return 'Riset Web · Perplexity';
  if (m.includes('grok') || m.includes('x_search')) return 'Riset X · Grok';
  if (m.includes('image') || m.includes('flux')) return 'Gambar · FLUX';
  // Web deck builder (model key "deck-builder") bills as a web presentation.
  // Checked BEFORE the legacy pptx branch (kept for old messages); no other
  // packaged key contains "deck", so this can't shadow or be shadowed.
  if (m.includes('deck')) return 'Presentasi · Web';
  if (m.includes('pptx')) return 'Dokumen · PPTX';
  return shortModelName(model).replace(/\s*\(.*\)$/, '');
};

// Distribute the (already-margined) charged credits across models by their
// cost share - the user sees per-model economics in credits only; raw USD
// never renders, so the margin can't be reverse-engineered.
const creditShares = (
  credits: number,
  models: { cost_usd: number }[]
): number[] => {
  const total = models.reduce((s, m) => s + (m.cost_usd || 0), 0);
  if (!(total > 0)) return models.map((_, i) => (i === 0 ? credits : 0));
  const shares = models.map((m) => Math.floor((credits * (m.cost_usd || 0)) / total));
  let remainder = credits - shares.reduce((s, v) => s + v, 0);
  for (let i = 0; remainder > 0; i = (i + 1) % shares.length) {
    shares[i] += 1;
    remainder -= 1;
  }
  return shares;
};

// Renders "{n} kredit" for a message carrying per-answer usage data (from the
// SSE "usage" event). On desktop hover it reveals a small Obsidian panel listing
// each model used - shortened name, in/out tokens, cost. Render-only (no click
// handlers); the tooltip is suppressed on mobile. Messages without usage data
// don't render this at all (the meta row simply omits the credits label).
const CreditsMeta: React.FC<{
  c: any;
  credits: number;
  models?: { model: string; in_tokens: number; out_tokens: number; calls: number; cost_usd: number }[];
  isMobile: boolean;
}> = ({ c, credits, models, isMobile }) => {
  const [hover, setHover] = useState(false);
  const hasTooltip = !isMobile && !!(models && models.length > 0);
  const cell: React.CSSProperties = { fontFamily: c.mono, fontSize: '11px', whiteSpace: 'nowrap' };
  return (
    <span
      style={{ position: 'relative', fontFamily: c.mono, cursor: hasTooltip ? 'default' : 'inherit' }}
      onMouseEnter={hasTooltip ? () => setHover(true) : undefined}
      onMouseLeave={hasTooltip ? () => setHover(false) : undefined}
    >
      {credits} kredit
      {hasTooltip && hover && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: 0,
            zIndex: 30,
            // Solid bg with the translucent panel tint layered on top → readable
            // "panel" surface even over message text behind it.
            backgroundColor: c.bg,
            backgroundImage: `linear-gradient(${c.panel}, ${c.panel})`,
            border: `1px solid ${c.line}`,
            borderRadius: '10px',
            boxShadow: c.shadow,
            padding: '8px 10px',
            // The positioned ancestor is the tiny "N kredit" label, so
            // shrink-to-fit would squeeze the grid until the flexible Model
            // column collapsed to zero width. Size to content instead.
            width: 'max-content',
            maxWidth: '380px',
          }}
        >
          <span style={{ display: 'grid', gridTemplateColumns: 'auto auto auto', columnGap: '14px', rowGap: '5px', alignItems: 'baseline' }}>
            <span style={{ ...cell, color: c.ink3 }}>Model</span>
            <span style={{ ...cell, color: c.ink3, textAlign: 'right' }}>Token</span>
            <span style={{ ...cell, color: c.ink3, textAlign: 'right' }}>Kredit</span>
            {(() => {
              const shares = creditShares(credits, models!);
              return models!.map((m, i) => (
                <React.Fragment key={i}>
                  <span style={{ ...cell, color: c.ink, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '180px' }}>{packagedModelName(m.model)}</span>
                  <span style={{ ...cell, color: c.ink2, textAlign: 'right' }}>{(m.in_tokens + m.out_tokens).toLocaleString('id-ID')}</span>
                  <span style={{ ...cell, color: c.ink2, textAlign: 'right' }}>{shares[i]}</span>
                </React.Fragment>
              ));
            })()}
          </span>
        </span>
      )}
    </span>
  );
};

export function useMessageRenderer(
  dependencies: MessageRendererDependencies,
  callbacks: MessageRendererCallbacks
) {
  const {
    theme,
    currentThemeColors,
    messageStyles,
    copiedMessageId,
    branchingMessageId,
    copiedCode,
    isMobile,
    messages,
    textareaRef,
    getModelDisplayName,
    convertCitationsToMap,
    copyToClipboard,
    readOnly,
  } = dependencies;

  const {
    setCopiedMessageId,
    setInputMessage,
    handleBranchout,
    navigate,
  } = callbacks;

  const c = currentThemeColors;

  const messageKey = (m: MessageWithCitations) => m.created_at || String(m.id);

  // Suggestion-chip click: fill the composer + focus it (mirrors the welcome-
  // screen chip pattern in MainContent). Never auto-submits.
  const pickSuggestion = (text: string) => {
    setInputMessage(text);
    requestAnimationFrame(() => {
      const el = textareaRef?.current;
      if (el) {
        el.focus();
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
        el.setSelectionRange(text.length, text.length);
      }
    });
  };

  // --- thumbs feedback (session-only; keyed by server message id) ---
  // Only messages whose id is a real server id may send feedback. Client-side
  // placeholders (streaming) carry Date.now() ids, so we gate on the explicit
  // `fromServer` flag stamped in conversationService.processMessageFiles,
  // NOT on created_at, which client placeholders also set.
  const [feedbackById, setFeedbackById] = useState<Record<number, 1 | -1>>({});
  const canRateMessage = (m: MessageWithCitations): boolean =>
    !readOnly &&
    m.role === 'assistant' &&
    m.isComplete !== false &&
    m.fromServer === true &&
    typeof m.id === 'number' && m.id > 0 &&
    !(m.model || '').toLowerCase().includes('playbook');

  const sendFeedback = (message: MessageWithCitations, next: 1 | -1) => {
    const id = message.id;
    const prev = feedbackById[id];
    const rating: 1 | -1 | 0 = prev === next ? 0 : next; // click selected again → clear
    // Optimistic update
    setFeedbackById((s) => {
      const out = { ...s };
      if (rating === 0) delete out[id];
      else out[id] = rating;
      return out;
    });
    const token = localStorage.getItem('accessToken');
    const tokenType = localStorage.getItem('tokenType') || 'Bearer';
    fetch(`${BASE_URL}/conversations/messages/${id}/feedback`, {
      method: 'POST',
      headers: {
        'Authorization': `${tokenType} ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`feedback HTTP ${res.status}`);
      })
      .catch((err) => {
        console.warn('Message feedback failed, reverting:', err);
        // Revert optimistic state
        setFeedbackById((s) => {
          const out = { ...s };
          if (prev === undefined) delete out[id];
          else out[id] = prev;
          return out;
        });
      });
  };

  const copyMessage = (message: MessageWithCitations) => {
    let textToCopy = message.content;
    if (message.role === 'assistant' && message.sources && message.sources.length > 0) {
      textToCopy += '\n\nSources:\n' + message.sources.map((s, i) => `[${i + 1}] ${s}`).join('\n');
    }
    navigator.clipboard.writeText(textToCopy);
    setCopiedMessageId(messageKey(message));
    setTimeout(() => setCopiedMessageId(null), 1000);
  };

  // Obsidian meta row: spark + tok/s + kredit + Salin + Cabang.
  const renderMeta = (message: MessageWithCitations) => {
    // Live streaming tps (client-side) wins; on reloaded messages fall back to
    // the persisted tps_est → both cases render the same spark + speed row.
    const liveTps = typeof message.tps === 'number' && message.tps > 0 ? message.tps : null;
    const estTps = typeof message.tpsEst === 'number' && message.tpsEst > 0 ? Math.round(message.tpsEst) : null;
    const speed = liveTps ?? estTps;
    const speedLabel = liveTps != null ? `${liveTps} tok/s` : (estTps != null ? `~${estTps} tok/s` : '');
    // Per-message credits only exist for messages that received a "usage" event.
    // Old messages have none → the credits label is omitted (never a wrong static number).
    const hasCredits = typeof message.creditsCharged === 'number';
    const isCopied = copiedMessageId === messageKey(message);
    const isBranching = branchingMessageId === message.id;
    // Meta actions read as ink2 (not the faint row color) with a leading glyph so
    // they signal "clickable"; hover firms to full ink. On mobile they carry a
    // 36px tap target via padding, with no layout shift on desktop (padding 0).
    const actBtn: React.CSSProperties = {
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      background: 'none', border: 'none', margin: 0, cursor: 'pointer',
      color: c.ink2, fontSize: '11px', fontFamily: 'inherit',
      padding: isMobile ? '0 6px' : 0,
      minHeight: isMobile ? 36 : undefined,
      transition: 'color 150ms ease',
    };
    const glyph = {
      width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none',
      stroke: 'currentColor', strokeWidth: 1.7,
      strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
      style: { flexShrink: 0 } as React.CSSProperties,
    };
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: c.ink3, fontSize: '11px', flexWrap: 'wrap' }}>
        {speed != null && (
          <>
            <span style={{ color: c.jade, letterSpacing: '-1px' }}>▮▮▮</span>
            <span style={{ fontFamily: c.mono }}>{speedLabel}</span>
          </>
        )}
        {speed != null && hasCredits && <span>·</span>}
        {hasCredits && (
          <CreditsMeta c={c} credits={message.creditsCharged!} models={message.modelsUsed} isMobile={isMobile} />
        )}
        <span style={{ display: 'flex', gap: isMobile ? '4px' : '12px', alignItems: 'center' }}>
          <button
            style={actBtn}
            onClick={() => copyMessage(message)}
            onMouseEnter={(e) => { e.currentTarget.style.color = c.ink; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = c.ink2; }}
          >
            <svg {...glyph}><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
            {isCopied ? 'Tersalin' : 'Salin'}
          </button>
          {/* Cabang writes a new conversation (owner-only), so it is gated
              like the thumbs on a read-only shared view. Streaming
              placeholders have client ids and must not be sent. */}
          {!readOnly
            && message.fromServer === true
            && message.isComplete !== false
            && !(message.model || '').toLowerCase().includes('playbook') && (
            <button
              style={{ ...actBtn, cursor: isBranching ? 'not-allowed' : 'pointer' }}
              onClick={() => handleBranchout(message)}
              disabled={isBranching}
              title="Buka chat baru dari jawaban ini"
              onMouseEnter={(e) => { if (!isBranching) e.currentTarget.style.color = c.ink; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = c.ink2; }}
            >
              <svg {...glyph}>
                <circle cx="6" cy="6" r="2.2" />
                <circle cx="18" cy="6" r="2.2" />
                <circle cx="18" cy="18" r="2.2" />
                <path d="M8 6h8M7.2 7.6C8.6 9.8 10 12.2 10 15c0 1.1.9 2 2 2h4" />
              </svg>
              {isBranching ? 'Membuat cabang…' : 'Cabang'}
            </button>
          )}
        </span>
        {canRateMessage(message) && (() => {
          const current = feedbackById[message.id];
          const thumbBtn = (rating: 1 | -1, title: string, path: string) => {
            const selected = current === rating;
            return (
              <button
                title={title}
                aria-label={title}
                aria-pressed={selected}
                onClick={() => sendFeedback(message, rating)}
                className="pa-icon-ghost"
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '2px',
                  margin: 0,
                  borderRadius: '5px',
                  cursor: 'pointer',
                  display: 'grid',
                  placeItems: 'center',
                  // Consistent with Salin/Cabang: ink2 default (not the faint ink3),
                  // jade when selected, 36px tap target on mobile.
                  color: selected ? c.jade : c.ink2,
                  minHeight: isMobile ? 36 : undefined,
                  minWidth: isMobile ? 36 : undefined,
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill={selected ? c.jadeDim : 'none'}
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d={path} />
                </svg>
              </button>
            );
          };
          return (
            <span style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {thumbBtn(1, 'Jawaban membantu',
                'M7 10v12M15 5.88L14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88z')}
              {thumbBtn(-1, 'Jawaban kurang membantu',
                'M17 14V2M9 18.12L10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H20a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-2.76a2 2 0 0 0-1.79 1.11L12 22a3.13 3.13 0 0 1-3-3.88z')}
            </span>
          );
        })()}
      </div>
    );
  };

  // The tail is the ACTION under an error, not a second copy of it: the body
  // above already rendered message.content. It only appears for the personal
  // "upgrade your plan" refusal, where it turns the sentence's tail into a link
  // to /subscription. Every other error (a team wallet running dry, a network
  // failure) returns null here - rendering content again printed the whole
  // sentence twice, which was invisible only because the one refusal anybody
  // ever hit happened to match the upgrade phrase.
  const renderErrorTail = (message: MessageWithCitations) => {
    if (!message.error) return null;
    const UPGRADE_PHRASE = 'Please upgrade your plan to get more credits';
    if (!message.content.includes(UPGRADE_PHRASE)) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
        <span>
          {message.content.split(UPGRADE_PHRASE)[1]}
          <a
            href="/subscription"
            style={{ color: c.jade, textDecoration: 'underline', cursor: 'pointer' }}
            onClick={(e) => { e.preventDefault(); navigate('/subscription'); }}
          >
            Upgrade Plan
          </a>
        </span>
      </div>
    );
  };

  const isImageName = (filename: string) => /\.(jpg|jpeg|png|gif)$/i.test(filename);

  // Click delegation for markdown images injected via dangerouslySetInnerHTML:
  // intercept clicks on .pa-md-img thumbnails and open the in-app lightbox
  // (ImageLightbox, mounted once in Chat.tsx) instead of the anchor fallback.
  const handleBodyClick = (e: React.MouseEvent) => {
    const t = e.target as HTMLElement;
    if (t instanceof HTMLImageElement && t.classList.contains('pa-md-img')) {
      e.preventDefault();
      e.stopPropagation();
      window.dispatchEvent(new CustomEvent('pa:lightbox', {
        detail: { src: t.src, alt: t.alt, el: t },
      }));
    }
  };

  // Body: file-attachment previews for user messages, else markdown content.
  const renderBody = (message: MessageWithCitations) => {
    if (message.role === 'user' && message.content.includes('[File:')) {
      const fname = message.content.match(/\[File: ([^]*?)(?=])/)?.[1] || '';
      return (
        <div style={{ whiteSpace: 'pre-wrap' }}>
          {message.content.split('[File:')[0]}
          {isImageName(fname) ? (
            message.fileUrls && message.fileUrls.length > 0 ? (
              <div style={{ maxWidth: '400px', margin: '10px 0 5px 0', borderRadius: '8px', overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: message.fileUrls.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
                  {message.fileUrls.map((url, index) => (
                    <img key={index} src={url} alt={`Uploaded file ${index + 1}`} style={{ width: '100%', height: 'auto', objectFit: 'contain', borderRadius: '4px' }} />
                  ))}
                </div>
              </div>
            ) : message.fileUrl ? (
              <div style={{ maxWidth: '400px', margin: '10px 0 5px 0', borderRadius: '8px', overflow: 'hidden' }}>
                <img src={message.fileUrl} alt="Uploaded file" style={{ width: '100%', height: 'auto', objectFit: 'contain' }} />
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                <svg width="20" height="20" fill="currentColor"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" /></svg>
                <span>{fname}</span>
              </div>
            )
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
              <svg width="20" height="20" fill="currentColor"><path d="M6 2h9l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z" /></svg>
              <span>{fname}</span>
            </div>
          )}
        </div>
      );
    }
    return (
      <div
        className="pa-body"
        onClick={handleBodyClick}
        style={{
          // Normal white-space: the processor converts every source newline
          // explicitly (block-margin / 10px spacer / <br>), so no invisible
          // blank bands can stack on top of element margins.
          whiteSpace: 'normal',
          lineHeight: 1.65,
          // Token vars consumed by the injected markdown HTML (headings, bold,
          // inline code, hr, images) so both themes stay token-driven.
          ['--pa-ink' as any]: c.ink,
          ['--pa-ink3' as any]: c.ink3,
          ['--pa-line' as any]: c.line,
          ['--pa-line2' as any]: c.line2,
          ['--pa-panel2' as any]: c.panel2,
        }}
      >
        {processContent({
          content: message.content,
          theme,
          copiedCode: copiedCode || undefined,
          onCopy: copyToClipboard,
          citationsMap: convertCitationsToMap(message.citations),
        })}
      </div>
    );
  };

  const renderMessage = (message: MessageWithCitations) => {
    // --- image-generation model messages ---
    if (isImageGenerationModel(message.model)) {
      if (message.role === 'assistant' && message.fileUrl) {
        return <GeneratedImageMessage message={message} theme={theme} retrieveFile={retrieveFile} />;
      }
      if (message.role === 'user') {
        return (
          <UserMessageWithImage
            message={message}
            theme={theme}
            messageStyle={message.role === 'user' ? messageStyles.user : messageStyles.assistant}
            retrieveFile={retrieveFile}
            copiedMessageId={copiedMessageId}
            setCopiedMessageId={setCopiedMessageId}
          />
        );
      }
    }

    // --- user message: plain text (bubble is provided by the container) ---
    if (message.role === 'user') {
      return (
        <div style={{
          fontSize: '14.5px',
          ...(message.error && { color: theme === 'light' ? '#991B1B' : '#FCA5A5' }),
        }}>
          {renderBody(message)}
        </div>
      );
    }

    // --- assistant message ---
    // Dedupe by domain (keep the FIRST url per hostname) so the chip row doesn't
    // show three chips that all read "kompas.com". Runs before SourceChips slices
    // to its MAX, so the visible chips are distinct domains.
    const rawSourceUrls: string[] = message.sources && message.sources.length
      ? message.sources
      : (message.citations ? Array.from(message.citations) : []);
    const seenSourceDomains = new Set<string>();
    const sourceUrls: string[] = [];
    for (const u of rawSourceUrls) {
      const d = domainOf(u);
      if (seenSourceDomains.has(d)) continue;
      seenSourceDomains.add(d);
      sourceUrls.push(u);
    }
    const complete = message.isComplete !== false;
    const hasTrace = !!(message.steps && message.steps.length > 0);
    const isLarge = isLargeTable(message.content);
    // Suggestion chips render only on the thread's final message (so they vanish
    // once a new turn is appended), after completion, and never on errors.
    const isLastMessage = messages.length > 0 && messages[messages.length - 1] === message;
    const showSuggestions = isLastMessage && complete && !message.error
      && !!(message.suggestions && message.suggestions.length > 0);

    return (
      <div style={{
        display: 'flex', flexDirection: 'column', gap: '10px',
        color: theme === 'dark' ? '#C9CCD2' : c.ink,
        fontSize: '14.5px',
        ...(message.error && {
          background: theme === 'light' ? '#FEF2F2' : '#3B1818',
          border: `1px solid ${theme === 'light' ? '#FCA5A5' : '#991B1B'}`,
          color: theme === 'light' ? '#991B1B' : '#FCA5A5',
          borderRadius: '12px',
          padding: '14px 16px',
        }),
      }}>
        <AssistantByline c={c} name={getModelDisplayName(message.model)} when={formatClock(message.created_at)} />

        {/* Trace card while researching, or restyled Thinking fallback for light answers */}
        {hasTrace ? (
          <WorkBar c={c} message={message} />
        ) : (!complete && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: c.ink2 }}>
            <span style={{ width: '7px', height: '7px', background: c.jade, borderRadius: '50%', animation: 'pulse 1.4s ease-in-out infinite' }} />
            Berpikir…
          </div>
        ))}

        {/* Body */}
        {isLarge ? (
          <div
            className="pa-body"
            onClick={handleBodyClick}
            style={{
              whiteSpace: 'normal',
              lineHeight: 1.65,
              ['--pa-ink' as any]: c.ink,
              ['--pa-ink3' as any]: c.ink3,
              ['--pa-line' as any]: c.line,
              ['--pa-line2' as any]: c.line2,
              ['--pa-panel2' as any]: c.panel2,
            }}
          >
            <LazyTable content={message.content} theme={theme} currentThemeColors={c} />
          </div>
        ) : (
          renderBody(message)
        )}

        {/* Source chips (after completion) */}
        {complete && sourceUrls.length > 0 && <SourceChips c={c} urls={sourceUrls} />}

        {/* Memori transparency chip: what the save_memory tool stored this
            turn. Client-only (vanishes on reload, like the live trace). */}
        {message.memorySaved && message.memorySaved.length > 0 && (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: '3px',
            fontSize: '11.5px', color: c.ink3, fontFamily: c.mono,
          }}>
            {message.memorySaved.map((m, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={c.jade} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                </svg>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  Disimpan ke memori: {m}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Error tail (upgrade link etc.) */}
        {renderErrorTail(message)}

        {/* Meta row (after completion) */}
        {complete && !message.error && renderMeta(message)}

        {/* Follow-up suggestion chips (last message only, after completion) */}
        {showSuggestions && (
          <SuggestionChips c={c} items={message.suggestions!} onPick={pickSuggestion} />
        )}
      </div>
    );
  };

  return { renderMessage };
}
